import fulfill from '../../fulfill';
import { SideEffectFunction } from '../../sideEffects';
import { CHECKOUT_API_HOSTNAME } from '../../env';
import { CheckoutConfig, Transaction } from '../../types/checkout';
import {
    ActionTypes,
    FetchCheckoutConfigurationResponse,
    FetchNextResponse,
    FetchSessionsResponse,
    GetCheckoutConfiguration,
    GetSessionReason,
    GetSessions
} from './actions';

const fetchSessions: SideEffectFunction<GetSessions, FetchSessionsResponse> = async (action) => {
    const params = action.payload.params;
    const result: FetchSessionsResponse = await fulfill.get({
        accountId: action.payload.accountId,
        url: `${CHECKOUT_API_HOSTNAME}/v1/sessions?${params.toString()}`,
        handlers: {
            200: (sessions: Transaction[]) => ({
                type: ActionTypes.FetchSessionsResponse,
                payload: {
                    sessions: sessions,
                },
                meta: {
                    cause: action,
                },
            }),
            400: () => ({
                type: ActionTypes.FetchSessionsResponse,
                payload: {
                    sessions: [],
                },
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

const fetchNext: SideEffectFunction<FetchSessionsResponse, FetchNextResponse | undefined> = async (action) => {
    if (action.meta.cause.payload.reason === GetSessionReason.refresh) {
        // no need to check next page if the causeing action was a "refresh"
        return;
    }
    const hasLimitInParams = action.meta.cause.payload.params.get('limit');
    const isAtLastPage = hasLimitInParams
        ? action.payload.sessions.length < parseInt(hasLimitInParams)
        : action.payload.sessions.length === 0;
    if (isAtLastPage) {
        return {
            type: ActionTypes.FetchNextResponse,
            payload: {
                sessions: [],
            },
            meta: {
                cause: action,
            },
        };
    }
    const params = action.meta.cause.payload.params;
    const lastTransaction = action.payload.sessions[action.payload.sessions.length - 1];
    params.delete('starting_after');
    if (lastTransaction && lastTransaction.id) {
        params.append('starting_after', lastTransaction.id);
    }
    params.delete('limit');
    params.append('limit', '1');
    const result: FetchNextResponse = await fulfill.get({
        accountId: action.meta.cause.payload.accountId,
        url: `${CHECKOUT_API_HOSTNAME}/v1/sessions?${params.toString()}`,
        handlers: {
            200: (sessions: Transaction[]) => ({
                type: ActionTypes.FetchNextResponse,
                payload: {
                    sessions: sessions,
                },
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

const fetchGetCheckoutConfiguration: SideEffectFunction<
    GetCheckoutConfiguration,
    FetchCheckoutConfigurationResponse
> = async (action) => {
    const promises = action.payload.accountIds.map((accountId) =>
        fulfill.get({
            accountId: accountId,
            url: `${CHECKOUT_API_HOSTNAME}/v1/admin/checkout`,
            handlers: {
                200: (configuration: CheckoutConfig[]) => ({
                    configuration,
                    accountId,
                }),
                404: () => ({
                    configuration: undefined,
                    accountId,
                }),
            },
        }));
    const results = await Promise.all(promises);
    return {
        type: ActionTypes.FetchCheckoutConfigurationResponse,
        payload: {
            configurations: results.reduce((acc, result) => {
                if (!result || !result.configuration) {
                    return acc;
                }
                return {
                    ...acc,
                    [result.accountId]: result.configuration,
                };
            }, {}),
        },
        meta: {
            cause: action,
        },
    };
};

const effects = {
    [ActionTypes.GetSessions]: fetchSessions,
    [ActionTypes.FetchSessionsResponse]: fetchNext,
    [ActionTypes.GetCheckoutConfiguration]: fetchGetCheckoutConfiguration,
};

export default effects;
